import { ArrowsClockwise, Plus, UploadSimple, X } from 'phosphor-react';
import { MAX_BATCH_FILES } from '../constants';
import CustomSelect from './CustomSelect';

function UploadPanel({
  queuedFiles,
  isDropOverSelect,
  setIsDropOverSelect,
  isDropOverAdd,
  setIsDropOverAdd,
  addFilesInputRef,
  handleQueueFileChange,
  handleDropFiles,
  handleQueuedFormatChange,
  handleRemoveQueuedFile,
  handleAddFilesClick,
  handleCreateJob,
  getOutputsForFile,
  isSubmitting,
  uploadProgress,
  activeUploadFileName
}) {
  return (
    <section className="relative z-20 overflow-visible rounded-3xl border border-white/60 bg-white/85 p-6 shadow-xl shadow-brand-100/40 backdrop-blur">
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <img src="/apps/format-converter/logo/logo.webp" alt="Format Converter logo" className="h-10 w-10 rounded-xl object-cover" />
          <h1 className="text-lg font-bold sm:text-xl">Format Converter</h1>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-slate-600 sm:text-base">
          Upload media, pick a target format, and convert files with cloud processing optimized for web playback.
        </p>
      </div>

      <form className="grid gap-4" onSubmit={handleCreateJob}>
        {queuedFiles.length === 0 ? (
          <label
            className={`relative inline-flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-10 text-sm font-semibold text-slate-800 transition hover:shadow-md ${
              isDropOverSelect
                ? 'border-brand-600 bg-brand-50 shadow-md'
                : 'border-brand-500/50 bg-white hover:border-brand-500'
            }`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDropOverSelect(true);
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsDropOverSelect(true);
            }}
            onDragLeave={() => setIsDropOverSelect(false)}
            onDrop={(event) => handleDropFiles(event, 'select')}
          >
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              onChange={handleQueueFileChange}
            />
            <span className="inline-flex items-center gap-2">
              <UploadSimple size={16} weight="bold" />
              Select files
            </span>
            <span className="text-center text-xs font-medium text-slate-500">
              Add one or more files. You can choose output format for each file after selection.
            </span>
            <span className="text-center text-xs font-medium text-slate-500">Supports image and video files.</span>
          </label>
        ) : (
          <div className="grid gap-2">
            <p className="text-sm font-semibold text-slate-700">Selected files</p>
            {queuedFiles.map((entry) => {
              const outputs = getOutputsForFile(entry.file);
              return (
                <div
                  key={entry.id}
                  className="grid grid-cols-[minmax(0,1fr)_130px_auto] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 sm:grid-cols-[minmax(0,1fr)_220px_auto]"
                >
                  <p className="truncate text-sm font-medium text-slate-800" title={entry.file.name}>
                    {entry.file.name}
                  </p>
                  <div className="min-w-0">
                    <CustomSelect
                      options={outputs}
                      value={entry.format}
                      onChange={(nextFormat) => handleQueuedFormatChange(entry.id, nextFormat)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveQueuedFile(entry.id)}
                    className="inline-flex shrink-0 items-center justify-center rounded-lg bg-slate-100 p-2 text-slate-600 transition hover:bg-slate-200"
                    aria-label={`Remove ${entry.file.name}`}
                    title="Remove file"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mx-auto flex w-full max-w-[460px] flex-wrap items-center justify-center gap-3 sm:flex-nowrap">
          <input
            ref={addFilesInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="hidden"
            onChange={handleQueueFileChange}
          />
          {queuedFiles.length > 0 && (
            <button
              type="button"
              onClick={handleAddFilesClick}
              className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 sm:w-56 ${
                isDropOverAdd
                  ? 'bg-brand-100 text-brand-800 ring-2 ring-brand-300'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              disabled={queuedFiles.length >= MAX_BATCH_FILES}
              title={queuedFiles.length >= MAX_BATCH_FILES ? `Max ${MAX_BATCH_FILES} files per batch` : 'Add files'}
              onDragOver={(event) => {
                if (queuedFiles.length >= MAX_BATCH_FILES) return;
                event.preventDefault();
                setIsDropOverAdd(true);
              }}
              onDragEnter={(event) => {
                if (queuedFiles.length >= MAX_BATCH_FILES) return;
                event.preventDefault();
                setIsDropOverAdd(true);
              }}
              onDragLeave={() => setIsDropOverAdd(false)}
              onDrop={(event) => {
                if (queuedFiles.length >= MAX_BATCH_FILES) return;
                handleDropFiles(event, 'add');
              }}
            >
              <Plus size={16} weight="bold" />
              Add files
            </button>
          )}
          <button
            className="w-full rounded-xl bg-brand-500 px-4 py-4 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-300 sm:w-56"
            type="submit"
            disabled={isSubmitting || !queuedFiles.length}
          >
            <span className="inline-flex items-center justify-center gap-2">
              <ArrowsClockwise size={16} weight="bold" className={isSubmitting ? 'animate-spin' : ''} />
              {isSubmitting ? 'Creating' : `Convert${queuedFiles.length ? ` (${queuedFiles.length})` : ''}`}
            </span>
          </button>
        </div>
        <p className="mx-auto w-full max-w-[460px] text-center text-xs text-slate-500">
          Batch limit: up to {MAX_BATCH_FILES} files per convert.
        </p>

        {!!uploadProgress && uploadProgress < 100 && (
          <p className="text-sm text-slate-600">Uploading {activeUploadFileName || 'file'}: {uploadProgress}%</p>
        )}
      </form>
    </section>
  );
}

export default UploadPanel;
